import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { CancelReasonCode, DeclineReasonCode, OrderStatus, SellerOrderDetail } from "@/lib/api/types";
import { endpoints } from "@/lib/api/endpoints";
import { apiJsonClient } from "@/lib/query/core/api-json-client";
import { patchSellerOrderInCache } from "@/lib/query/core/invalidation";
import { queryKeys } from "@/lib/query/core/query-keys";

type UpdateSellerOrderStatusInput = {
  storeSlug: string;
  orderId: string;
  status: OrderStatus;
  statusEtaDays: number | null;
  declineReasonCode?: DeclineReasonCode;
  declineReasonNote?: string;
  cancelReasonCode?: CancelReasonCode;
  cancelReasonNote?: string;
};

/** Opaque snapshot of query caches used for optimistic rollback. */
type MutationContext = {
  snapshots: [unknown, unknown][];
};

export function useUpdateOrderStatusMutation() {
  const queryClient = useQueryClient();
  const mutation = useMutation<
    SellerOrderDetail,
    Error,
    UpdateSellerOrderStatusInput,
    MutationContext
  >({
    mutationFn: (input: UpdateSellerOrderStatusInput) => {
      const { storeSlug, orderId, ...body } = input;
      void storeSlug;
      return apiJsonClient<SellerOrderDetail>(endpoints.seller.orderStatus(orderId), {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
    },

    // M2 — optimistic update: cancel in-flight queries, snapshot, then patch.
    onMutate: async ({ storeSlug, orderId, status, statusEtaDays }) => {
      // Cancel any outstanding fetches so they don't overwrite our optimistic patch.
      await queryClient.cancelQueries({
        queryKey: queryKeys.seller.ordersPrefix(storeSlug),
      });
      await queryClient.cancelQueries({
        queryKey: queryKeys.seller.orderById(storeSlug, orderId),
        exact: true,
      });

      // Snapshot the affected caches for potential rollback.
      const listSnapshots = queryClient.getQueriesData<unknown>({
        queryKey: queryKeys.seller.ordersPrefix(storeSlug),
      });
      const detailSnapshot = queryClient.getQueryData<unknown>(
        queryKeys.seller.orderById(storeSlug, orderId),
      );
      // Combine into a single flat list that rollback can iterate over.
      const snapshots: [unknown, unknown][] = [
        ...listSnapshots,
        [queryKeys.seller.orderById(storeSlug, orderId), detailSnapshot],
      ];

      // Apply the optimistic patch into every relevant cache shape.
      patchSellerOrderInCache(queryClient, storeSlug, orderId, {
        status,
        statusEtaDays: statusEtaDays ?? null,
      });

      return { snapshots };
    },

    // M2 — rollback on error: restore every snapshotted cache entry.
    onError: (_err, _vars, context) => {
      if (!context) return;
      for (const [key, data] of context.snapshots) {
        queryClient.setQueryData(key as Parameters<typeof queryClient.setQueryData>[0], data);
      }
    },

    // M2 — on settled: only invalidate the status-count buckets (membership
    // genuinely shifts).  The list rows and detail are already up-to-date via
    // the optimistic patch + the server response merged by React Query's
    // default behaviour.
    //
    // Safety-net note: we intentionally do NOT call invalidateSellerOrders here
    // because that would nuke the whole ordersPrefix namespace and trigger a
    // full refetch, defeating the purpose of M1/M2.  If a future cache shape
    // isn't covered by patchSellerOrderInCache, add it to that helper rather
    // than falling back to full invalidation.
    onSettled: async (_data, _err, variables) => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.seller.orderStatusCounts(variables.storeSlug),
        exact: true,
      });
      // Also invalidate the proof since it's unrelated to the patch shape.
      await queryClient.invalidateQueries({
        queryKey: queryKeys.seller.orderProof(variables.storeSlug, variables.orderId),
        exact: true,
      });
    },
  });

  return {
    mutateAsync: mutation.mutateAsync,
  };
}
